import {css} from '@emotion/react'
import styled from '@emotion/styled'

import mirror from '@vio/atlas/src/utils/mirror'
import {cssTheme} from '@vio/atlas/themes'

export const HEADER_HEIGHT = 58
export const DAY_SIZE = 44

export const HORIZONTAL_PICKER_MIN_WIDTH = 600
export const HORIZONTAL_PICKER_MAX_WIDTH = 802

const baseStyles = css`
  position: relative;
  z-index: 0;
  .wrapper {
    height: 100%;
  }
  .month-wrapper {
    flex: 1 1 0%;
  }
  &.--is-vertical {
    width: 100%;
    height: 100%;
    padding: 0 ${cssTheme.layout.spacing.s200};
    padding-bottom: ${cssTheme.layout.spacing.s500};
  }
  &:not(.--is-vertical) {
    width: ${HORIZONTAL_PICKER_MAX_WIDTH}px;
    margin-bottom: ${cssTheme.layout.spacing.s200};
    &.--is-single-month {
      width: 100%;
    }
  }
`

const monthsWrapperStyles = css`
  .months-wrapper {
    height: 100%;
  }
  &.--is-vertical:not(.--is-single-month) {
    .months-wrapper {
      user-select: none;
      width: 100%;
      border: ${cssTheme.layout.spacing.s300} solid transparent;
    }
  }

  &:not(.--is-vertical):not(.--is-single-month) {
    .months-wrapper {
      display: flex;
      flex: 1;
      &:before {
        content: '';
        width: 1px;
        top: ${HEADER_HEIGHT}px;
        height: calc(100% - ${HEADER_HEIGHT}px);
        background-color: ${cssTheme.colors.border.neutral.secondary};
        position: absolute;
        left: 50%;
      }
    }
  }
`

const monthCaptionStyles = css`
  &:not(.--is-vertical) {
    .caption-wrapper {
      position: relative;
      border-bottom: 1px solid ${cssTheme.colors.border.neutral.secondary};
    }
    &.--is-single-month {
      .caption-wrapper {
        border-bottom: 0;
      }
    }
  }
`

const navButtonsStyles = css`
  &:not(.--is-vertical) {
    .nav-button {
      z-index: 1;
      &.nav-button-next {
        right: 0;
      }
      &.nav-button-previous {
        left: 0;
      }
      position: absolute;
      top: 0;
      margin: 0;
      border: none;
      box-shadow: none;
      outline: none;
      font: inherit;
      background: transparent;
      padding: 0 ${cssTheme.layout.spacing.s700};
      height: ${HEADER_HEIGHT}px;
      cursor: pointer;
      color: ${cssTheme.colors.datePicker.navButton.content};
      &[aria-disabled='true'] {
        color: ${cssTheme.colors.datePicker.navButton.disabled.content};
        cursor: not-allowed;
      }
      ${mirror()}
    }
    &.--is-single-month {
      .nav-button {
        padding: 0 ${cssTheme.layout.spacing.s600};
      }
    }
  }
`

const weekDaysStyles = css`
  .week-day-wrapper {
    .--is-vertical & {
      display: table-cell;
      width: auto;
      text-align: center;
      margin: 0;
    }
    .week-day-inner {
      ${cssTheme.typography.bodyS};
      color: ${cssTheme.colors.content.neutral.secondary};
      text-align: center;
      width: ${DAY_SIZE}px;
      margin: ${cssTheme.layout.spacing.s400} 0;
      &.--is-weekend {
        background-color: ${cssTheme.colors.datePicker.weekends.background};
        border-radius: ${cssTheme.layout.radius.sm};
      }
    }
  }
  &.--is-vertical {
    .calendar-week-days-wrapper {
      display: none;
    }
  }
`

const monthDaysStyles = css`
  .calendar-wrapper {
    width: 100%;
    table-layout: fixed;
    border-spacing: ${cssTheme.layout.spacing.s200};
    padding: 0;
  }
  &:not(.--is-vertical):not(.--is-single-month) {
    .calendar-wrapper {
      padding: 0 17px;
    }
  }
`

const dayCellStyles = css`
  .day-cell {
    border: none;
    background-color: transparent;
    ${cssTheme.typography.bodyS}
    display: table-cell;
    position: relative;
    text-align: center;
    cursor: pointer;
    vertical-align: middle;
    padding: 0;
    outline: none;
    border-radius: ${cssTheme.layout.radius.lg};
    height: ${DAY_SIZE}px;
    width: 100%;
    color: ${cssTheme.colors.content.neutral.primary};
  }
  .today > .day-cell {
    color: ${cssTheme.colors.datePicker.today.content};
  }
  &:not(.--is-vertical) {
    .day-cell {
      width: ${DAY_SIZE}px;
    }
  }
`

const weekendsStyles = css`
  .weekends > button {
    background: ${cssTheme.colors.datePicker.weekends.background};
  }
`

const disabledDaysStyles = css`
  .disabled > button {
    cursor: default;
    color: ${cssTheme.colors.content.neutral.disabled};
    .loading {
      display: none;
    }
  }
`

const selectedDaysStyles = css`
  .selected > button {
    background: ${cssTheme.colors.datePicker.selectedDay.background};
    color: ${cssTheme.colors.datePicker.selectedDay.content};
    border-radius: 0;

    &:after,
    &:before {
      content: '';
      display: block;
      position: absolute;
      height: 100%;
      width: 4px;
      top: 0;
      background: ${cssTheme.colors.datePicker.selectedDay.background};
    }

    &:before {
      left: -4px;
    }

    &:after {
      right: -4px;
    }
  }
`

const startRangeDayStyles = css`
  .start > button {
    border-start-start-radius: ${cssTheme.layout.radius.lg};
    border-start-end-radius: 0;
    border-end-end-radius: 0;
    border-end-start-radius: ${cssTheme.layout.radius.lg};
    &:before {
      display: none;
    }
    .day-inner {
      &:after {
        right: -4px;
        border-left: 4px solid
          ${cssTheme.colors.datePicker.startEndRangeDay.background};
      }
    }
    &:hover .day-inner {
      &:after {
        border-left-color: ${cssTheme.colors.datePicker.startEndRangeDay.hover
          .background};
      }
    }
  }
  &.--is-single-day-selected {
    .start > button {
      border-radius: ${cssTheme.layout.radius.lg};
      &:after {
        display: none;
      }
    }
  }
`

const endRangeDayStyles = css`
  .end > button {
    border-start-start-radius: 0;
    border-start-end-radius: ${cssTheme.layout.radius.lg};
    border-end-end-radius: ${cssTheme.layout.radius.lg};
    border-end-start-radius: 0;
    &:after {
      display: none;
    }

    .day-inner {
      &:after {
        left: -4px;
        border-right: 4px solid
          ${cssTheme.colors.datePicker.startEndRangeDay.background};
      }
    }
    &:hover .day-inner {
      &:after {
        border-right-color: ${cssTheme.colors.datePicker.startEndRangeDay.hover
          .background};
      }
    }
  }
`

const startEndRangeDayStyles = css`
  .startEnd > button {
    z-index: 2;
    padding: 0;

    .day-inner {
      background: ${cssTheme.colors.datePicker.startEndRangeDay.background};
      color: ${cssTheme.colors.datePicker.startEndRangeDay.content};
      border-radius: ${cssTheme.layout.radius.lg};
      z-index: 1;
      position: relative;

      width: ${DAY_SIZE}px;
      height: ${DAY_SIZE}px;
      line-height: ${DAY_SIZE}px;

      &:after {
        content: '';
        display: block;
        position: absolute;
        width: 0;
        top: 7px;
        z-index: 1;
        background: transparent;
        border-top: 15px solid transparent;
        border-bottom: 15px solid transparent;
      }
    }
    &:hover .day-inner {
      background: ${cssTheme.colors.datePicker.startEndRangeDay.hover
        .background};
    }
  }
  &.--is-vertical {
    .startEnd > button .day-inner {
      width: 100%;
    }
  }
`

export const BaseDatePicker = styled.div`
  ${baseStyles}
  ${navButtonsStyles}
  ${monthsWrapperStyles}
  ${monthCaptionStyles}
  ${weekDaysStyles}
  ${monthDaysStyles}
  ${dayCellStyles}
  ${disabledDaysStyles}
`

export const DatePickerElement = styled(BaseDatePicker)`
  ${weekendsStyles}
  ${selectedDaysStyles}
  ${startEndRangeDayStyles}
  ${startRangeDayStyles}
  ${endRangeDayStyles}
`
